'use client';

import { PlayIcon } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/utils';

import { ImageZoom } from './kibo-ui/image-zoom';

export function YouTubeEmbed({
  videoId,
  title,
}: {
  videoId: string;
  title: string;
}) {
  const [isPlaying, setIsPlaying] = useState(false);

  return (
    <div className="relative my-[1.25em]">
      {isPlaying ? (
        <iframe
          className="aspect-video w-full rounded-xl"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          className="group/youtube relative block aspect-video w-full cursor-pointer overflow-hidden rounded-xl"
          onClick={() => setIsPlaying(true)}
          aria-label={`Play video: ${title}`}
        >
          {/* biome-ignore lint/performance/noImgElement: cross-origin YouTube thumbnail used as a lazy facade */}
          <img
            className="size-full object-cover"
            src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
            alt={title}
            loading="lazy"
          />

          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex size-14 items-center justify-center rounded-full bg-black/70 transition-colors group-hover/youtube:bg-black/85">
              <PlayIcon className="size-6 fill-white text-white" aria-hidden />
            </span>
          </span>
        </button>
      )}

      <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-black/10 ring-inset dark:ring-white/10" />
    </div>
  );
}

export function IframeEmbed({
  className,
  ...props
}: React.ComponentProps<'iframe'>) {
  return (
    <div className="relative my-[1.25em]">
      <iframe
        className={cn('aspect-video w-full rounded-xl', className)}
        loading="lazy"
        {...props}
      />

      <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-black/10 ring-inset dark:ring-white/10" />
    </div>
  );
}

export function FramedImage({
  canZoom = true,
  alt,
  ...props
}: React.ComponentProps<'img'> & {
  canZoom?: boolean;
}) {
  // biome-ignore lint/performance/noImgElement: ImageZoom requires img element
  const image = <img alt={alt} {...props} />;

  return (
    <figure className="relative [&_img]:rounded-xl">
      {canZoom ? <ImageZoom>{image}</ImageZoom> : image}

      <div className="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-black/10 ring-inset dark:ring-white/10" />
    </figure>
  );
}
